import { CButton, CCard, CCardBody } from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import TaxPayer from "../../models/tax-payer";
import { logOut } from "../../redux/actions/user";
import {
  setTaxPayer,
  setTaxPayerLoaded,
  setTaxPayerLoading,
  setUserTaxPayers,
} from "../../redux/tax-payer/tax-payer-slice";
import { RootState } from "../../store";
import { rememberSelectedTaxPayer } from "../../utils/tax-payer-selection";

/**
 * Shown instead of the whole app while the selected company is suspended.
 * Nothing of the company is reachable from here; someone with access to other
 * companies can move to one of them.
 */
const TaxPayerSuspended = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.user.id);
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );
  const userTaxPayers = useSelector(
    (state: RootState) => state.taxPayer.data.userTaxPayers,
  );
  const otherTaxPayers = userTaxPayers.filter(
    (company) => company.id !== taxPayer.id && !company.accessBlocked,
  );
  const suspension = taxPayer.suspension;
  const companyName = taxPayer.fantasyName || taxPayer.socialName;

  const switchTo = (company: TaxPayer) => {
    if (!userId || company.id === undefined) return;
    rememberSelectedTaxPayer(userId, company.id);
    dispatch(setTaxPayer(company));
  };

  const handleLogout = () => {
    dispatch(logOut());
    dispatch(setTaxPayerLoaded(false));
    dispatch(setTaxPayerLoading(false));
    dispatch(setUserTaxPayers([]));
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center p-3"
      style={{ minHeight: "100vh" }}
    >
      <CCard style={{ maxWidth: "560px", width: "100%" }}>
        <CCardBody className="text-center p-4">
          <i
            className="fa fa-ban text-danger mb-3"
            style={{ fontSize: "48px" }}
            aria-hidden="true"
          />
          <h3>Cuenta suspendida</h3>
          {companyName && <p className="text-muted mb-3">{companyName}</p>}
          <p role="alert">
            {suspension?.message ??
              "La cuenta de la empresa se encuentra suspendida."}
          </p>
          {suspension?.reasonName && (
            <p className="mb-1">
              <strong>Motivo:</strong> {suspension.reasonName}
            </p>
          )}
          {suspension?.suspendedAt && (
            <p>
              <strong>Desde:</strong>{" "}
              {new Date(suspension.suspendedAt).toLocaleDateString()}
            </p>
          )}
          <p className="text-muted small">
            Sus documentos y datos se conservan y volverán a estar disponibles
            al reactivarse la cuenta.
          </p>
          {otherTaxPayers.length > 0 && (
            <div className="mt-4">
              <p className="mb-2">Puede continuar con otra empresa:</p>
              {otherTaxPayers.map((company) => (
                <CButton
                  key={company.id}
                  color="primary"
                  variant="outline"
                  className="m-1"
                  onClick={() => switchTo(company)}
                >
                  {company.fantasyName || company.socialName || company.ruc}
                </CButton>
              ))}
            </div>
          )}
          <CButton color="secondary" className="mt-4" onClick={handleLogout}>
            Cerrar sesión
          </CButton>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default TaxPayerSuspended;
